import {
  PrintJobFeedbackError,
  runPrintImageByLocationWithFeedback,
  runPrintPdfByLocationWithFeedback,
} from "@/lib/printJob";
import { setDefaultPrintLocation } from "@/lib/printPreferences";
import { fetchSchwarzesBrettFileBlob, type SchwarzesBrettFile } from "@/lib/schwarzesBrett";

/** Druckbar sind alle Dateien, die auch eine Vorschau haben (PDF, Office → PDF, Bilder). */
export function canPrintFile(file: Pick<SchwarzesBrettFile, "preview">): boolean {
  return file.preview !== "none";
}

function pdfName(name: string): string {
  return /\.pdf$/i.test(name) ? name : `${name.replace(/\.[^.]+$/, "")}.pdf`;
}

/**
 * Datei am gewählten Druckort (CUPS) drucken. Office-Dateien werden serverseitig
 * nach PDF konvertiert, Bilder über den Bild-Druckpfad ausgegeben.
 *
 * `preloaded` erlaubt die Wiederverwendung eines bereits für die Vorschau
 * geladenen Blobs (spart die zweite Konvertierung).
 */
export async function printSchwarzesBrettFile(
  file: SchwarzesBrettFile,
  locationId: number,
  copies: number,
  preloaded?: Blob | null,
): Promise<void> {
  if (!canPrintFile(file)) {
    throw new Error("Dieser Dateityp kann nicht direkt gedruckt werden.");
  }
  await setDefaultPrintLocation(locationId);
  try {
    if (file.preview === "image") {
      const blob = preloaded ?? (await fetchSchwarzesBrettFileBlob(file.id, "original"));
      await runPrintImageByLocationWithFeedback(locationId, copies, blob, {
        filename: file.name,
        loadingMessage: `${file.name} wird gedruckt…`,
      });
      return;
    }
    const format = file.preview === "convert" ? "pdf" : "original";
    const blob = preloaded ?? (await fetchSchwarzesBrettFileBlob(file.id, format));
    await runPrintPdfByLocationWithFeedback(locationId, copies, blob, {
      filename: pdfName(file.name),
      loadingMessage:
        file.preview === "convert"
          ? `${file.name} wird umgewandelt und gedruckt…`
          : `${file.name} wird gedruckt…`,
    });
  } catch (err) {
    if (err instanceof PrintJobFeedbackError) {
      throw new Error(err.feedback.message || err.feedback.title || "Druck fehlgeschlagen.");
    }
    throw err;
  }
}
